import { PieChart, Pie, Cell, Label, Sector } from 'recharts'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import { ChartSkeleton } from './ChartSkeleton'
import { ChartError } from './ChartError'
import { EmptyChart } from './EmptyChart'
import { usePacketsByStatus } from '../hooks/usePacketsByStatus'
import { PieSectorDataItem } from 'recharts/types/polar/Pie'

export function PacketsByStatusChart({ className }: { className?: string }) {
  const { data, isLoading, error } = usePacketsByStatus()

  if (isLoading) return <ChartSkeleton className={className} />
  if (error) return <ChartError className={className} />
  if (!data?.length) return <EmptyChart message="No packets by status data available" className={className} />

  const total = data.reduce((sum, d) => sum + d.count, 0)

  const chartConfig = {
    draft: {
      label: 'Draft',
      color: 'hsl(var(--chart-1))', // Primary teal
    },
    blindedAssessment: {
      label: 'Blinded Assessment',
      color: 'hsl(var(--chart-2))', // Primary coral
    },
    unblindedAssessment: {
      label: 'Unblinded Assessment',
      color: 'hsl(var(--chart-3))', // Darker teal
    },
    managerSummary: {
      label: 'Manager Summary',
      color: 'hsl(var(--chart-4))', // Darker coral
    },
    seniorLeaderReview: {
      label: 'Senior Leader Review',
      color: 'hsl(var(--chart-5))', // Lighter teal
    },
    hrReview: {
      label: 'HR Review',
      color: 'hsl(var(--chart-6))', // Light coral
    },
    completed: {
      label: 'Completed',
      color: 'hsl(var(--chart-7))', // Very dark teal
    },
  }

  const colors = [
    'hsl(var(--chart-1))', // Primary teal
    'hsl(var(--chart-2))', // Primary coral
    'hsl(var(--chart-3))', // Darker teal
    'hsl(var(--chart-4))', // Darker coral
    'hsl(var(--chart-5))', // Lighter teal
    'hsl(var(--chart-6))', // Light coral
    'hsl(var(--chart-7))', // Very dark teal
  ]

  // Map data to match expected format with human-friendly names
  const chartData = data.map((item, idx) => ({
    name: item.status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()),
    value: item.count,
    fill: colors[idx % colors.length]
  }))

  // Find the index of the slice with the highest value (majority slice)
  const activeIndex = chartData.reduce((maxIdx, current, idx, arr) => 
    current.value > arr[maxIdx].value ? idx : maxIdx, 0
  )

  return (
    <div className={className}>
      <div className="mb-4">
        <h3 className="text-xl font-semibold">Packets by Status</h3>
        <p className="text-base text-muted-foreground">Current year packets grouped by status</p>
      </div>
      <ChartContainer
        config={chartConfig}
        className="mx-auto aspect-square w-full max-w-[300px] h-[300px]"
      >
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0];
                return (
                  <div className="rounded-lg border bg-background p-2 shadow-md">
                    <div className="flex items-center gap-2">
                      <div 
                        className="h-2 w-2 rounded-sm" 
                        style={{ backgroundColor: data.payload.fill }}
                      />
                      <span className="text-xs">
                        {data.name}
                      </span>
                      <span className="text-xs font-semibold ml-1">
                        {data.value}
                      </span>
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            innerRadius={80}
            outerRadius={120}
            strokeWidth={5}
            paddingAngle={data.length > 1 ? 5 : 0}
            activeIndex={activeIndex}
            activeShape={({ outerRadius = 0, ...props }: PieSectorDataItem) => (
              <Sector {...props} outerRadius={outerRadius + 15} />
            )}
          >
            {chartData.map((entry, idx) => (
              <Cell key={`cell-${idx}`} fill={entry.fill} />
            ))}
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={viewBox.cy}
                        className="fill-foreground text-7xl font-bold"
                      >
                        {total.toLocaleString()}
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + 40}
                        className="fill-muted-foreground text-sm"
                      >
                        PACKETS
                      </tspan>
                    </text>
                  )
                }
                return null
              }}
              position="center"
            />
          </Pie>
        </PieChart>
      </ChartContainer>
    </div>
  )
} 